// CMP-TopBar. Maquette : 64 px, fond surface, recherche (max 480 px) avec « Ctrl K », pastille « Vue : <rôle> »
// (ton action), notifications, messagerie, aide, puis avatar 36 px + nom + rôle. Lot F7a : recherche globale active
// (SYS-P01, Ctrl/⌘ K place le curseur) et cloche ALR-D01 chargée à la demande (budget JS initial). Lot F8 : bouton Messagerie
// avec compteur de non lus (COM-07). Langue et thème : menu du profil
// et SYS-P04 (écart avec la spec CMP-TopBar, conforme à la maquette haute fidélité).
import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Bell, CircleHelp, Download, History, LogOut, Menu, MessageSquare, Search, Settings2, ShieldCheck, User } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { signOut } from '@/app/auth';
import type { Me } from '@/app/me';
import { IconButton } from '@/design-system/Button';
import { Avatar } from '@/design-system/Avatar';
import { ROUTES } from '@/navigation/nav-config';

const NotificationBell = lazy(() => import('@/modules/alerts/NotificationBell'));
// Messagerie (lot F8) : compteur de non lus chargé à la demande.
const MessagingButton = lazy(() => import('@/modules/comms/UnreadIndicators').then((m) => ({ default: m.MessagingButton })));

/** Recherche globale (SYS-P01) : Entrée ouvre les résultats ; Ctrl/⌘ K place le curseur dans le champ. */
function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = value.trim();
    if (q.length > 0) void navigate({ to: '/recherche', search: { q } });
  };

  return (
    <form role="search" onSubmit={submit} className="flex h-10 max-w-[480px] flex-1 items-center gap-2.5 rounded-md border border-border-strong px-3 text-small text-text-2 focus-within:outline-2 focus-within:outline-focus max-md:hidden">
      <Search aria-hidden="true" className="size-[18px] flex-none" />
      <input
        ref={input}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label={t('topbar.search_label')}
        placeholder={t('topbar.search_placeholder')}
        className="min-w-0 flex-1 border-0 bg-transparent text-small text-text outline-none placeholder:text-text-2"
      />
      <kbd className="rounded-sm border border-border px-1.5 font-mono whitespace-nowrap">{t('topbar.search_shortcut')}</kbd>
    </form>
  );
}

export interface TopBarProps {
  me: Me;
  onOpenNavigation?: () => void;
}

export function TopBar({ me, onOpenNavigation }: TopBarProps) {
  const { t } = useTranslation();
  const { t: ts } = useTranslation('states');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roleLabel = me.primary_role?.label ?? me.roles[0]?.label ?? '';
  const soon = ts('coming_soon.body');

  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-surface px-6 max-md:px-2">
      {onOpenNavigation && <IconButton icon={Menu} label={t('actions.open_menu')} onClick={onOpenNavigation} className="min-[900px]:hidden" />}

      <SearchBox />

      {roleLabel && (
        // DECISION-PENDING: ARB-20 — pastille du profil principal ; sélecteur si plusieurs rôles.
        <span className="rounded-sm bg-action-bg px-2.5 py-1 text-small font-semibold whitespace-nowrap text-action max-lg:hidden">
          {t('topbar.view_as')} {roleLabel}
        </span>
      )}

      <div className="ml-auto flex items-center gap-3 text-text-2">
        <IconButton icon={Search} label={t('topbar.search_mobile')} onClick={() => void navigate({ to: '/recherche' })} className="md:hidden" />
        <Suspense fallback={<IconButton icon={Bell} label={t('topbar.notifications')} />}>
          <NotificationBell />
        </Suspense>
        {me.permissions.includes('comms.view') && (
          <span className="max-md:hidden">
            <Suspense fallback={<IconButton icon={MessageSquare} label={t('topbar.messages')} />}>
              <MessagingButton />
            </Suspense>
          </span>
        )}
        <IconButton icon={CircleHelp} label={`${t('topbar.help')} — ${soon}`} aria-disabled="true" className="cursor-not-allowed max-md:hidden" />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" aria-label={t('topbar.profile_menu')} className="flex cursor-pointer items-center gap-2 rounded-md p-1 text-text hover:bg-surface-2">
              <Avatar name={me.full_name} src={me.avatar_url} size={36} />
              <span className="flex flex-col text-left text-small leading-[18px] max-md:hidden">
                <strong>{me.full_name}</strong>
                <span className="text-text-2">{roleLabel}</span>
              </span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end" sideOffset={8} className="z-50 min-w-56 rounded-lg border border-border bg-surface p-1 text-text shadow-e3">
              <DropdownMenu.Label className="px-3 py-2 text-small">
                <strong className="block">{me.full_name}</strong>
                <span className="text-text-2">{me.email}</span>
              </DropdownMenu.Label>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              {[
                { to: ROUTES.profile, icon: User, label: t('topbar.my_profile') },
                { to: ROUTES.security, icon: ShieldCheck, label: t('topbar.security') },
                { to: ROUTES.myActivity, icon: History, label: t('topbar.my_activity') },
                { to: ROUTES.preferences, icon: Settings2, label: t('topbar.preferences') },
                { to: ROUTES.myExports, icon: Download, label: t('nav:items.my_exports') },
              ].map((entry) => (
                <DropdownMenu.Item key={entry.to} asChild onSelect={() => void navigate({ to: entry.to })}>
                  <Link to={entry.to} className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-small text-text no-underline outline-none data-[highlighted]:bg-surface-2">
                    <entry.icon aria-hidden="true" className="size-4 text-text-2" />
                    {entry.label}
                  </Link>
                </DropdownMenu.Item>
              ))}
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={() => {
                  void signOut(queryClient)
                    .catch(() => undefined)
                    .then(() => navigate({ to: '/deconnexion' }));
                }}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-small text-text outline-none data-[highlighted]:bg-surface-2"
              >
                <LogOut aria-hidden="true" className="size-4 text-text-2" />
                {t('topbar.sign_out')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
